import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Chip, Grid, CircularProgress, Card, CardContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cotizacionVentaService, {
  CotizacionVenta,
  CotizacionVentaDetalle,
  type CotizacionPdfPrecargaResult,
} from '../../services/cotizacionVentaService';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import ubicacionService from '../../services/ubicacionService';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel, { type POSCompaniaOption } from '../facturaventa/pos/FacturaPOSTotalesPanel';
import FacturaPOSNuevoClienteDialog from '../facturaventa/pos/FacturaPOSNuevoClienteDialog';
import PrecargaCotizacionPdfDialog from '../../components/cotizacionventa/PrecargaCotizacionPdfDialog';
import CorregirArticulosGenericosPdfDialog, {
  type CorreccionArticuloGenerico,
} from '../../components/cotizacionventa/CorregirArticulosGenericosPdfDialog';
import Can from '../../components/Can';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { cargarProductoParaDocumento } from '../../utils/bloqueaArtDiscontinuados';
import {
  contextoDescuentoDesdeCliente,
  recalcularDescuentosVenta,
  resolverBonificacionesCliente,
  resolverPorcDescLinea,
} from '../../services/descuentoService';
import { formatNroDocumentoVenta, parseLocalDate, toLocalDateStringYMD, truncToDecimals } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { useCan } from '../../hooks/useCan';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import {
  filtrarSucursalesPorCompania,
  resolveNroSucParaCompania,
  resolveSucursalPreferidaNro,
  resolveCompaniaYSucursalInicial,
  persistCompaniaYSucursalInicial,
} from '../../utils/sucursalPreferida';
import { persistGlobalIdCompania } from '../../utils/companiaPreferida';
import companiaService from '../../services/companiaService';
import {
  calcularFechaVencimDesdeVtoComprob,
  sameCalendarDay,
  toLocalDateInputValue,
  validarFechasFacturaVenta,
} from '../../utils/facturaVentaFechas';
import { mapearVencimComprobanteCargado } from '../../utils/facturaVentaVencim';
import { resolveVtoComprobCliente } from '../../utils/vtoComprobCliente';
import { calcularTotalesIvaVenta } from '../../utils/calcularTotalesIvaVenta';
import { normalizarTipoIvaReceptor } from '../../utils/getIvaRateVenta';
import {
  aplicarPorcentajeDescuento,
  esTipoIvaPreciosConIva,
  multiplicadorIvaProducto,
  round2,
  subtotalLineaConIvaDisplay,
} from '../facturaventa/pos/preciosIvaDisplay';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { COTIZACIONES_POS_TOUR } from '../../config/tours/cotizacionesVentaTour';

type CotizacionVentaUI = CotizacionVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type CotizacionVentaUIExt = CotizacionVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; dni?: string; idCompania?: string };

/**
 * Cotizaciones: reutiliza normalizarTipoIvaReceptor y además acepta descripciones
 * del maestro/UI (p. ej. "No Categorizado") para no caer en el default del 21%.
 */
const normalizarTipoIvaEmisionCotizacion = (tipoIva?: string | null): string => {
  const codigo = normalizarTipoIvaReceptor(tipoIva);
  if (codigo === 'I' || codigo === 'C' || codigo === 'M' || codigo === 'E' || codigo === 'N') {
    return codigo;
  }
  const tipo = String(tipoIva ?? '').trim().toUpperCase();
  if (!tipo) return '';
  if (tipo.includes('MONOTRIBUTO')) return 'M';
  if (tipo.includes('EXENTO')) return 'E';
  if (tipo.includes('CONSUMIDOR') || tipo.includes('FINAL')) return 'C';
  if (tipo.includes('CATEGORIZADO') || tipo.includes('NO INSCRIPT')) return 'N';
  if (tipo.includes('RESPONSABLE') || tipo.includes('INSCRIPT')) return 'I';
  return codigo;
};

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
/** Fallback al cargar cotización existente sin sucursal en el GET. */
const DEFAULT_NRO_SUC = 1;
/** Cotización nueva: 0 hasta que el combo tome la primera sucursal de la lista cargada. */
const NRO_SUC_COTIZACION_NUEVA = 0;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') return '';
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

const CotizacionesVenta: React.FC = () => {
  useRegisterPageTour(COTIZACIONES_POS_TOUR);
  const navigate = useNavigate();
  const { cotizacionId } = useParams<{ cotizacionId?: string }>();
  const location = useLocation();
  const {
    sucursales,
    preferidaNro,
    preferidaSucursal,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();
  const sucursalUsuarioEditadaRef = useRef(false);
  const initCompaniaSucursalRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadingCotizacion, setLoadingCotizacion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);
  const [companias, setCompanias] = useState<POSCompaniaOption[]>([]);
  const loadingSucursales = loadingSucursalesUsuario;
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const recalcDescSeqRef = useRef(0);
  const [precargaPdfOpen, setPrecargaPdfOpen] = useState(false);
  const [precargaPdfLoading, setPrecargaPdfLoading] = useState(false);
  const [precargaPdf, setPrecargaPdf] = useState<CotizacionPdfPrecargaResult | null>(null);
  const [correccionGenericosOpen, setCorreccionGenericosOpen] = useState(false);
  const [nuevoClienteDesdePdfOpen, setNuevoClienteDesdePdfOpen] = useState(false);

  const modo = cotizacionId ? (location.pathname.includes('/ver') ? 'ver' : 'editar') : 'crear';
  const [cotizacionVenta, setCotizacionVenta] = useState<CotizacionVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '10',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    fechaVencim: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: NRO_SUC_COTIZACION_NUEVA,
    cuit: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const isEditMode = modo === 'editar';
  /** Hay ID en la URL (editar/ver): no sobrescribir cabecera con defaults del cliente (p. ej. vendedor). */
  const esCotizacionPersistente = Boolean(cotizacionId);
  const isReadOnlyByPath = modo === 'ver';
  const isReadOnlyByEstado = (cotizacionVenta.estado === 'Facturado' || cotizacionVenta.estado === 'Preparado') && isEditMode;
  const isReadOnly = isReadOnlyByPath || isReadOnlyByEstado;
  const puedeAltaCotizacion = useCan('ventas.cotizaciones.nuevo');
  /** Alta sin permiso: el backend la rechazaría, así que no se permite guardar. */
  const sinPermisoAlta = modo === 'crear' && !puedeAltaCotizacion;

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroCotizacionCreada, setNroCotizacionCreada] = useState<string | null>(null);
  const [productosIvaCache, setProductosIvaCache] = useState<Record<string, unknown>>({});
  const { enqueueSnackbar } = useSnackbar();

  const registrarProductoIva = useCallback((codigoArticulo: string, producto: unknown) => {
    const cod = String(codigoArticulo ?? '').trim();
    if (!cod || !producto) return;
    setProductosIvaCache((prev) => {
      if (prev[cod] === producto) return prev;
      return { ...prev, [cod]: producto };
    });
  }, []);

  useEffect(() => {
    if (cotizacionId) {
      const cargarCotizacion = async () => {
        try {
          setLoadingCotizacion(true);
          setError(null);
          const id = parseInt(cotizacionId, 10);
          if (isNaN(id)) {
            setError('ID de cotizacion invalido');
            navigate('/ventas/cotizaciones');
            return;
          }

          const cotizacionCargada = await cotizacionVentaService.getCotizacionVenta(id);
          let ubicacionFormateada = (cotizacionCargada as any).ubicacion || '';
          const nroUbicacionOrigen = (cotizacionCargada as any).nroUbicacionOrigen || (cotizacionCargada as any).nroUbicacion;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = ubicacionEncontrada
                ? `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (cotizacionCargada as any).transporte || '';
          const nroExpreso = (cotizacionCargada as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = cotizacionCargada.detalles?.map((detalle) => {
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            const subtotal = detalle.subtotal || 0;
            return { ...detalle, precioUnitario: precioLista, precioLista, descuento: porcDesc, porcDesc, subtotal };
          }) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => sum + (d.subtotal || 0), 0);
          const fechaEmisionDate = cotizacionCargada.fechaEmision ? (cotizacionCargada.fechaEmision instanceof Date ? cotizacionCargada.fechaEmision : new Date(cotizacionCargada.fechaEmision)) : new Date();
          const vencimComprobante = mapearVencimComprobanteCargado(cotizacionCargada, fechaEmisionDate);
          const horaEmisionDate = cotizacionCargada.horaEmision ? (cotizacionCargada.horaEmision instanceof Date ? cotizacionCargada.horaEmision : new Date(cotizacionCargada.horaEmision)) : new Date();

          const nroVendedorCargado = Number(
            (cotizacionCargada as any).nroVendedor ?? (cotizacionCargada as any).NroVendedor ?? 0,
          );

          setCotizacionVenta({
            ...cotizacionCargada,
            fechaEmision: fechaEmisionDate,
            fechaVencim: vencimComprobante.fechaVencim,
            vtoComprob: vencimComprobante.vtoComprob,
            horaEmision: horaEmisionDate,
            ordenCompra: (cotizacionCargada as any).ordenDeCompra || (cotizacionCargada as any).ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (cotizacionCargada as any).formaDePago || '',
            nroSuc: (cotizacionCargada as any).nroSuc || DEFAULT_NRO_SUC,
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
            cuit: (cotizacionCargada as any).cuit || '',
            tipoIVA: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            dni: (cotizacionCargada as any).dni || '',
            idCompania: (cotizacionCargada as any).idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: cotizacionCargada.importeTotal && cotizacionCargada.importeTotal > 0 ? cotizacionCargada.importeTotal : round2(importeTotalCalculado),
          } as CotizacionVentaUIExt);

          setPosCliente({
            nroCliente: Number(cotizacionCargada.nroCliente || 0),
            nombre: cotizacionCargada.nombre || '',
            cuit: (cotizacionCargada as any).cuit || '',
            dni: (cotizacionCargada as any).dni || '',
            tipoIva: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
          });
          setLineasPOS(
            detallesConSubtotales.map((d, idx) => ({
              id: `${cotizacionCargada.nroFormulario || 'cot'}-${idx + 1}`,
              codigoArticulo: d.codigoArticulo || '',
              descripcionArticulo: d.descripcionArticulo || '',
              cantidad: Number(d.cantidad || 0),
              precioUnitario: Number(d.precioUnitario || d.precioLista || 0),
              descuento: Number(d.descuento || d.porcDesc || 0),
              descuentoManual: true,
              subtotal: Number(d.subtotal || 0),
              um: d.um || '',
              notas: d.notas,
            })),
          );

          const codigos: string[] = Array.from(
            new Set(
              detallesConSubtotales
                .map((d) => String(d.codigoArticulo ?? '').trim())
                .filter((c) => c.length > 0),
            ),
          );
          const fetchedProducts: Record<string, unknown> = {};
          await Promise.all(
            codigos.map(async (codigo) => {
              try {
                const producto = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
                if (producto) fetchedProducts[codigo] = producto;
              } catch {
                /* sin producto */
              }
            }),
          );
          if (Object.keys(fetchedProducts).length > 0) {
            setProductosIvaCache(fetchedProducts);
          } else {
            setProductosIvaCache({});
          }
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Error al cargar la cotizacion');
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/cotizaciones'), 2000);
        } finally {
          setLoadingCotizacion(false);
        }
      };
      cargarCotizacion();
    }
  }, [cotizacionId, navigate, enqueueSnackbar]);

  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const lista = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
        setCompanias(
          lista
            .map((c) => ({
              idCompania: String(c.idCompania ?? '').trim(),
              nombre: String(c.nombre ?? '').trim() || undefined,
              condIva: c.condIva != null ? String(c.condIva).trim() : undefined,
            }))
            .filter((c) => Boolean(c.idCompania)),
        );
      } catch {
        setCompanias([]);
      }
    };
    void cargarCompanias();
  }, []);

  useEffect(() => {
    if (cotizacionId) {
      sucursalUsuarioEditadaRef.current = true;
      initCompaniaSucursalRef.current = true;
      return;
    }
    sucursalUsuarioEditadaRef.current = false;
    initCompaniaSucursalRef.current = false;
    setCotizacionVenta((prev) => ({
      ...prev,
      nroSuc: 0,
      idCompania: '',
    }));
  }, [cotizacionId, location.key]);

  useEffect(() => {
    if (cotizacionId || loadingCotizacion) return;
    if (loadingSucursalesUsuario) return;
    if (sucursales.length === 0 || companias.length === 0) return;
    if (sucursalUsuarioEditadaRef.current) return;
    if (initCompaniaSucursalRef.current) return;

    const inicial = resolveCompaniaYSucursalInicial({
      sucursales,
      preferidaNro,
      preferidaSucursal,
      companias,
    });
    if (!inicial) return;
    initCompaniaSucursalRef.current = true;
    setCotizacionVenta((prev) => ({
      ...prev,
      nroSuc: inicial.nroSuc,
      idCompania: inicial.idCompania,
    }));
    persistCompaniaYSucursalInicial(inicial);
  }, [
    sucursales,
    companias,
    cotizacionId,
    loadingCotizacion,
    loadingSucursalesUsuario,
    preferidaNro,
    preferidaSucursal,
    location.key,
  ]);

  const sucursalesDeCompania = React.useMemo(
    () => filtrarSucursalesPorCompania(sucursales, cotizacionVenta.idCompania),
    [sucursales, cotizacionVenta.idCompania],
  );

  const handleIdCompaniaChange = React.useCallback(
    (id: string) => {
      if (isReadOnly) return;
      const next = String(id ?? '').trim();
      if (next) persistGlobalIdCompania(next);
      const nro = resolveNroSucParaCompania({
        idCompania: next,
        sucursales,
        preferidaNro,
        storedNroSuc: Number(localStorage.getItem('nroSuc') || '0') || null,
        keepCurrentIfValid: false,
      });
      sucursalUsuarioEditadaRef.current = false;
      setCotizacionVenta((prev) => ({
        ...prev,
        idCompania: next,
        nroSuc: nro != null && nro > 0 ? nro : 0,
      }));
      if (nro != null && nro > 0) persistGlobalNroSuc(nro);
    },
    [isReadOnly, sucursales, preferidaNro],
  );

  // Solo persistir la preferida (no la elección manual del comprobante)
  useEffect(() => {
    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida != null) {
      persistGlobalNroSuc(preferida);
    }
  }, [preferidaNro, sucursales]);

  const handleClienteSelect = (cliente: any) => {
    const posCli = cliente as POSCliente;
    setPosCliente(posCli);
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    const vtoComprobCliente = resolveVtoComprobCliente(cliente);
    setCotizacionVenta((prev: CotizacionVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      vtoComprob: esCotizacionPersistente ? prev.vtoComprob : vtoComprobCliente,
      fechaVencim: esCotizacionPersistente
        ? (prev.fechaVencim ?? calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), prev.vtoComprob))
        : calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), vtoComprobCliente),
      nroVendedor:
        esCotizacionPersistente || isReadOnly
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      formaDePago: formaPagoSugerida,
      transporte: transporteSugerido,
    }));
    const nroSel = cliente.nroCliente ?? cliente.codigo ?? 0;
    void resolverBonificacionesCliente(cliente, {
      codigoLista: obtenerCodigoListaCliente(cliente),
      nroVendedor: Number(cliente?.nroVendedor ?? cliente?.vendedor) || null,
    }).then((desc) => {
      setCotizacionVenta((prev) => {
        if (Number(prev.nroCliente) !== Number(nroSel)) return prev;
        return { ...prev, porcDesc: desc.porcDesc, porcDesc2: desc.porcDesc2, porcDesc3: desc.porcDesc3 };
      });
    });
  };

  const handlePrecargaPdfPick = () => {
    pdfInputRef.current?.click();
  };

  const handlePrecargaPdfSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPrecargaPdfOpen(true);
    setPrecargaPdfLoading(true);
    setPrecargaPdf(null);
    try {
      const result = await cotizacionVentaService.precargarDesdePdf(file);
      setPrecargaPdf(result);
    } catch (error: unknown) {
      setPrecargaPdfOpen(false);
      enqueueSnackbar(getApiErrorMessage(error) || 'No se pudo precargar el PDF', { variant: 'error' });
    } finally {
      setPrecargaPdfLoading(false);
    }
  };

  const aplicarLineasDesdePrecarga = (precarga: CotizacionPdfPrecargaResult) => {
    const nuevas: POSLinea[] = (precarga.items || [])
      .filter((i) => i.cantidad > 0 && (i.precioUnitario > 0 || Boolean(i.codigoArticulo)))
      .map((i) => {
        const importePdf = Number(i.importeTotalConIva);
        const linea: POSLinea = {
          id: genId(),
          codigoArticulo: (i.codigoArticulo || '').trim(),
          descripcionArticulo: (i.descripcionOriginal || i.descripcion || '').trim(),
          cantidad: i.cantidad,
          precioUnitario: i.precioUnitario,
          descuento: 0,
          subtotal: 0,
          um: '',
          importeImportadoPdf: Number.isFinite(importePdf) && importePdf > 0 ? round2(importePdf) : undefined,
          notas: i.usoArticuloGenerico
            ? `Import PDF: ${i.descripcionOriginal || i.descripcion || ''}`
            : undefined,
        };
        linea.subtotal = linea.importeImportadoPdf ?? calcularSubtotal(linea);
        return linea;
      });
    setLineasPOS(nuevas);
  };

  const handleApplyPrecargaPdf = async () => {
    if (!precargaPdf) return;
    if ((precargaPdf.items || []).some((item) => item.usoArticuloGenerico)) {
      setCorreccionGenericosOpen(true);
      return;
    }
    await aplicarPrecargaEnFormulario(precargaPdf);
  };

  const handleConfirmarGenericos = (correcciones: CorreccionArticuloGenerico[]) => {
    if (!precargaPdf) return;
    const genericos = (precargaPdf.items || [])
      .map((item, index) => ({ item, index }))
      .filter((entry) => entry.item.usoArticuloGenerico);
    const items = (precargaPdf.items || []).map((item) => ({ ...item }));
    correcciones.forEach((corr) => {
      const destino = genericos[corr.indice];
      if (!destino) return;
      items[destino.index] = {
        ...items[destino.index],
        codigoArticulo: corr.codigoArticulo,
        descripcion: corr.descripcion,
        descripcionOriginal: corr.descripcion,
        usoArticuloGenerico: false,
        advertencia: null,
      };
    });
    const actualizada: CotizacionPdfPrecargaResult = { ...precargaPdf, items };
    setPrecargaPdf(actualizada);
    setCorreccionGenericosOpen(false);
    const sinCorregir = items.filter((item) => item.usoArticuloGenerico).length;
    if (sinCorregir > 0) {
      enqueueSnackbar(
        sinCorregir === 1
          ? 'Quedó 1 ítem con el artículo genérico.'
          : `Quedaron ${sinCorregir} ítems con el artículo genérico.`,
        { variant: 'warning' },
      );
    }
    void aplicarPrecargaEnFormulario(actualizada);
  };

  const aplicarPrecargaEnFormulario = async (precarga: CotizacionPdfPrecargaResult) => {
    const form = precarga.formulario;
    setCotizacionVenta((prev) => ({
      ...prev,
      ordenCompra: form?.ordenDeCompra || prev.ordenCompra,
      descripcion: form?.descripcion || prev.descripcion,
      formaDePago: form?.formaDePago
        ? normalizarFormaDePago(form.formaDePago) || form.formaDePago
        : prev.formaDePago,
      fechaEmision: form?.fechaEmision
        ? parseLocalDate(form.fechaEmision) || prev.fechaEmision
        : prev.fechaEmision,
      nroVendedor: form?.nroVendedor && form.nroVendedor > 0
        ? form.nroVendedor
        : prev.nroVendedor,
    }));

    if (precarga.cliente.encontrado && precarga.cliente.nroCliente) {
      try {
        const cli = await clienteService.getCliente(precarga.cliente.nroCliente);
        handleClienteSelect(cli);
      } catch {
        enqueueSnackbar('No se pudo cargar el cliente detectado', { variant: 'warning' });
      }
    }

    aplicarLineasDesdePrecarga(precarga);
    setPrecargaPdfOpen(false);
    setCorreccionGenericosOpen(false);
    enqueueSnackbar(
      `Precarga aplicada (${precarga.items?.length ?? 0} ítems). Revise y guarde.`,
      { variant: 'success' },
    );
  };

  const recalcularDescuentosContexto = (cliente: POSCliente | null, nroVendedor?: number | null) => {
    if (!cliente || isReadOnly) return;
    const seq = ++recalcDescSeqRef.current;
    void recalcularDescuentosVenta(
      cliente,
      { codigoLista: obtenerCodigoListaCliente(cliente), nroVendedor: nroVendedor ?? cliente.nroVendedor ?? null },
      lineasPOS,
    ).then(({ cabecera, porLinea }) => {
      if (seq !== recalcDescSeqRef.current) return;
      setCotizacionVenta((prev) => ({
        ...prev,
        porcDesc: cabecera.porcDesc,
        porcDesc2: cabecera.porcDesc2,
        porcDesc3: cabecera.porcDesc3,
      }));
      setLineasPOS((prev) =>
        prev.map((l) => {
          const porc = porLinea.get(l.id);
          if (porc == null || l.descuentoManual || porc === l.descuento) return l;
          const updated = { ...l, descuento: porc, importeImportadoPdf: undefined };
          updated.subtotal = calcularSubtotal(updated);
          return updated;
        }),
      );
    });
  };

  const handleAgregarProductoPOS = (item: SuggestionItem) => {
    const id = genId();
    const cod = String(item.codigoArticulo ?? '').trim();
    if (cod && item.productoIva) {
      registrarProductoIva(cod, item.productoIva);
    }
    setLineasPOS((prev) => [
      ...prev,
      {
        id,
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: item.precioUnitario,
        um: item.um,
        stockDisponible: item.stockDisponible,
      },
    ]);
    if (!cod) return;
    void (async () => {
      try {
        const producto = await cargarProductoParaDocumento(cod, 'VENTAS');
        registrarProductoIva(cod, producto);
        const porc = await resolverPorcDescLinea(
          contextoDescuentoDesdeCliente(posCliente, {
            codigoLista: obtenerCodigoListaCliente(posCliente),
            nroVendedor: posCliente?.nroVendedor,
          }),
          producto,
          1,
          String(item.um ?? ''),
        );
        setLineasPOS((prev) =>
          prev.map((l) => {
            if (l.id !== id || l.descuentoManual) return l;
            const updated = { ...l, descuento: porc, descuentoManual: false };
            updated.subtotal = calcularSubtotal(updated);
            return updated;
          }),
        );
      } catch {
        /* sin resolver */
      }
    })();
  };

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        if (field === 'descuento') updated.descuentoManual = true;
        if (field === 'cantidad' || field === 'precioUnitario' || field === 'descuento' || field === 'codigoArticulo') {
          updated.importeImportadoPdf = undefined;
        }
        updated.subtotal = updated.importeImportadoPdf ?? calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        if ('cantidad' in data || 'precioUnitario' in data || 'descuento' in data || 'codigoArticulo' in data) {
          updated.importeImportadoPdf = undefined;
        }
        updated.subtotal = updated.importeImportadoPdf ?? calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => {
    setLineasPOS((prev) => prev.filter((l) => l.id !== id));
  };

  const handleDuplicarLinea = (id: string) => {
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setLineasPOS((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '' },
    ]);
  };


  useEffect(() => {
    if (esCotizacionPersistente || isReadOnly) {
      return undefined;
    }
    const value = cotizacionVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            const vtoComprobCliente = resolveVtoComprobCliente(c);
            const desc = await resolverBonificacionesCliente(c, {
              codigoLista: obtenerCodigoListaCliente(c),
              nroVendedor: Number((c as any).nroVendedor ?? (c as any).vendedor) || null,
            });
            setCotizacionVenta((prev) => {
              const newCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                vtoComprobCliente,
              );
              const oldCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                prev.vtoComprob ?? null,
              );
              const mantenerVencimManual = !sameCalendarDay(
                prev.fechaVencim ?? prev.fechaEmision,
                oldCalc,
              );
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                vtoComprob: vtoComprobCliente,
                fechaVencim: mantenerVencimManual ? (prev.fechaVencim ?? newCalc) : newCalc,
                nroVendedor:
                  Number((c as any).vendedor) > 0 ? Number((c as any).vendedor) : 0,
                formaDePago: formaPagoSugerida,
                transporte: transporteSugerido,
                porcDesc: desc.porcDesc,
                porcDesc2: desc.porcDesc2,
                porcDesc3: desc.porcDesc3,
              } as CotizacionVentaUIExt;
            });
          } catch {
            /* ignore */
          }
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [cotizacionVenta.nroCliente, esCotizacionPersistente, isReadOnly]);

  useEffect(() => {
    const detalles = lineasPOS.map((l, idx): CotizacionVentaDetalle => ({
      nroIdformulario: cotizacionVenta.nroFormulario,
      nroItem: idx + 1,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      precioLista: l.precioUnitario,
      descuento: l.descuento,
      porcDesc: l.descuento,
      subtotal: l.subtotal,
      notas: l.notas,
      um: l.um,
    }));
    const importeTotal = detalles.reduce((acc, d) => acc + (d.subtotal || 0), 0);
    setCotizacionVenta((prev) => ({ ...prev, detalles, importeTotal: round2(importeTotal) }));
  }, [lineasPOS, cotizacionVenta.nroFormulario]);

  const tipoIvaEmision = normalizarTipoIvaEmisionCotizacion(
    posCliente?.tipoIva ?? cotizacionVenta.tipoIVA ?? '',
  );
  const preciosConIva = esTipoIvaPreciosConIva(tipoIvaEmision);
  const condIvaEmisor = useMemo(() => {
    const id = String(cotizacionVenta.idCompania ?? '').trim();
    return companias.find((c) => c.idCompania === id)?.condIva;
  }, [companias, cotizacionVenta.idCompania]);

  useEffect(() => {
    const codigos = Array.from(
      new Set(
        lineasPOS
          .map((l) => String(l.codigoArticulo ?? '').trim())
          .filter((c) => c.length > 0),
      ),
    );
    if (codigos.length === 0) return;

    let cancelled = false;
    (async () => {
      let missing: string[] = [];
      setProductosIvaCache((prev) => {
        missing = codigos.filter((c) => !prev[c]);
        return prev;
      });
      if (missing.length === 0) return;

      const fetched: Record<string, unknown> = {};
      await Promise.all(
        missing.map(async (codigo) => {
          try {
            const producto = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
            if (producto) fetched[codigo] = producto;
          } catch {
            /* sin producto */
          }
        }),
      );
      if (!cancelled && Object.keys(fetched).length > 0) {
        setProductosIvaCache((prev) => ({ ...prev, ...fetched }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lineasPOS]);

  const subtotalNetoPOS = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1POS = (subtotalNetoPOS * (cotizacionVenta.porcDesc || 0)) / 100;
  const baseDesc2POS = subtotalNetoPOS - importeDesc1POS;
  const importeDesc2POS = (baseDesc2POS * (cotizacionVenta.porcDesc2 || 0)) / 100;
  const baseDesc3POS = baseDesc2POS - importeDesc2POS;
  const importeDesc3POS = (baseDesc3POS * (cotizacionVenta.porcDesc3 || 0)) / 100;
  const subtotalConDescPOS = baseDesc3POS - importeDesc3POS;

  const totalesIva = useMemo(
    () =>
      calcularTotalesIvaVenta({
        lineas: lineasPOS.map((l) => ({
          codigoArticulo: l.codigoArticulo,
          subtotal: l.subtotal,
        })),
        subtotalNeto: subtotalNetoPOS,
        subtotalConDesc: subtotalConDescPOS,
        tipoIva: tipoIvaEmision,
        productosCache: productosIvaCache,
        condIvaEmisor,
      }),
    [
      lineasPOS,
      subtotalNetoPOS,
      subtotalConDescPOS,
      tipoIvaEmision,
      productosIvaCache,
      condIvaEmisor,
    ],
  );

  const { impuestosArray: impuestosPOS, montoIVA: ivaPOS, total: totalPOSBase } = totalesIva;

  const totalesDisplay = useMemo(() => {
    const subtotalDesdePdf = lineasPOS.some((l) => (l.importeImportadoPdf ?? 0) > 0);
    const subtotalDisplay = subtotalDesdePdf
      ? round2(
          lineasPOS.reduce((acc, l) => {
            if ((l.importeImportadoPdf ?? 0) > 0 && !(l.descuento > 0)) return acc + round2(l.importeImportadoPdf ?? 0);
            if (!preciosConIva) return acc + l.subtotal;
            const codigo = String(l.codigoArticulo ?? '').trim();
            const mult = multiplicadorIvaProducto(productosIvaCache[codigo], tipoIvaEmision, condIvaEmisor);
            return acc + subtotalLineaConIvaDisplay(l.precioUnitario, l.cantidad, l.descuento, mult);
          }, 0),
        )
      : preciosConIva
        ? round2(
            lineasPOS.reduce((acc, l) => {
              const codigo = String(l.codigoArticulo ?? '').trim();
              const mult = multiplicadorIvaProducto(productosIvaCache[codigo], tipoIvaEmision, condIvaEmisor);
              return acc + subtotalLineaConIvaDisplay(l.precioUnitario, l.cantidad, l.descuento, mult);
            }, 0),
          )
        : subtotalNetoPOS;
    const d1 = cotizacionVenta.porcDesc || 0;
    const d2 = cotizacionVenta.porcDesc2 || 0;
    const d3 = cotizacionVenta.porcDesc3 || 0;
    const importeDesc1Display = round2(subtotalDisplay - aplicarPorcentajeDescuento(subtotalDisplay, d1));
    const baseDesc2Display = aplicarPorcentajeDescuento(subtotalDisplay, d1);
    const importeDesc2Display = round2(baseDesc2Display - aplicarPorcentajeDescuento(baseDesc2Display, d2));
    const baseDesc3Display = aplicarPorcentajeDescuento(baseDesc2Display, d2);
    const importeDesc3Display = round2(baseDesc3Display - aplicarPorcentajeDescuento(baseDesc3Display, d3));
    const totalDisplay = aplicarPorcentajeDescuento(baseDesc3Display, d3);

    return {
      subtotalDisplay,
      importeDesc1Display,
      importeDesc2Display,
      importeDesc3Display,
      totalDisplay,
      subtotalDesdePdf,
    };
  }, [
    preciosConIva,
    lineasPOS,
    productosIvaCache,
    tipoIvaEmision,
    condIvaEmisor,
    subtotalNetoPOS,
    cotizacionVenta.porcDesc,
    cotizacionVenta.porcDesc2,
    cotizacionVenta.porcDesc3,
  ]);

  const totalPOS = truncToDecimals(
    totalesDisplay.subtotalDesdePdf || preciosConIva ? (totalesDisplay.totalDisplay ?? totalPOSBase) : totalPOSBase,
    2,
  );

  const handlePorcDescChangePOS = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setCotizacionVenta((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  const handleSave = async () => {
    if (sinPermisoAlta) {
      enqueueSnackbar('No tenés permiso para crear cotizaciones', { variant: 'warning' });
      return;
    }
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar una cotizacion en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!cotizacionVenta.nroCliente || cotizacionVenta.nroCliente <= 0) erroresValidacion.push('Debe seleccionar un cliente');
      if (!cotizacionVenta.fechaEmision) erroresValidacion.push('Debe ingresar la Fecha de Emision');
      const fechaVencimError = validarFechasFacturaVenta(
        cotizacionVenta.fechaEmision,
        cotizacionVenta.fechaVencim ?? cotizacionVenta.fechaEmision,
        calcularFechaVencimDesdeVtoComprob(
          cotizacionVenta.fechaEmision,
          cotizacionVenta.vtoComprob,
        ),
      );
      if (fechaVencimError) erroresValidacion.push(fechaVencimError);
      if (!cotizacionVenta.nroSuc || cotizacionVenta.nroSuc <= 0) {
        erroresValidacion.push('Debe seleccionar una Sucursal / Punto de Venta');
      }
      const ubicacion = (cotizacionVenta as CotizacionVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') erroresValidacion.push('Debe seleccionar una Ubicacion');
      const formaDePago = (cotizacionVenta as CotizacionVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!cotizacionVenta.nroSuc || cotizacionVenta.nroSuc <= 0) {
        erroresValidacion.push('Debe seleccionar una Sucursal / Punto de Venta');
      }
      if (!cotizacionVenta.detalles || cotizacionVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la cotizacion');
      } else {
        cotizacionVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene c?digo de art?culo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...cotizacionVenta,
        nroSuc: cotizacionVenta.nroSuc,
        ordenCompra: cotizacionVenta.ordenCompra || '',
        transporte: cotizacionVenta.transporte || '',
        ubicacion: cotizacionVenta.ubicacion || '',
        formaDePago: cotizacionVenta.formaDePago || '',
      } as any;

      let nroCotizacion: string | null = null;

      if (isEditMode && cotizacionId) {
        const id = parseInt(cotizacionId, 10);
        await cotizacionVentaService.updateCotizacionVenta(id, payload);
        enqueueSnackbar('Cotizacion actualizada exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/cotizaciones'), 1500);
        setLoading(false);
        return;
      } else {
        const response = await cotizacionVentaService.createCotizacionVenta(payload);
        const nroDoc = formatNroDocumentoVenta(response?.letra, response?.nroSuc, response?.nroComprobante);
        nroCotizacion = nroDoc || null;
      }

      if (nroCotizacion && !isEditMode) {
        setNroCotizacionCreada(nroCotizacion);
        setSuccessDialogOpen(true);
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          fechaVencim: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
        setProductosIvaCache({});
      } else {
        enqueueSnackbar('Cotizacion guardada exitosamente', { variant: 'success' });
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          fechaVencim: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
        setProductosIvaCache({});
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar la cotizacion';
      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        errorMessage = err.message;
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        errores.forEach((e: string) => { if (e.trim()) enqueueSnackbar(e.trim(), { variant: 'error', autoHideDuration: 6000 }); });
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
          else if (val) msgs.push(`${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          errorMessage = msgs.join('\n');
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
        }
      } else if (status === 400 || status === 500) {
        errorMessage = getApiErrorMessage(err, errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessContinuar = () => {
    setSuccessDialogOpen(false);
    setNroCotizacionCreada(null);
    navigate('/ventas/cotizaciones');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/cotizaciones')}
              sx={{ textTransform: 'none' }}
            >
              Cotizaciones
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {(() => {
                const nroDoc = formatNroDocumentoVenta(
                  cotizacionVenta.letra,
                  cotizacionVenta.nroSuc,
                  cotizacionVenta.nroComprobante,
                );
                if (isReadOnly) return nroDoc ? `Ver Cotización ${nroDoc}` : 'Ver Cotización';
                if (isEditMode) return nroDoc ? `Editar Cotización ${nroDoc}` : 'Editar Cotización';
                return 'Nueva Cotización POS';
              })()}
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          {sinPermisoAlta && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No tenés permiso para crear cotizaciones. Podés consultar el POS, pero no guardar cotizaciones.
            </Alert>
          )}

          {loadingCotizacion ? (
            <Box
              sx={{
                minHeight: '40vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Cargando cotización…
              </Typography>
            </Box>
          ) : (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Box data-tour-id="pos-cliente">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography variant="overline" color="text.secondary">
                    Cliente
                  </Typography>
                  {modo === 'crear' && !isReadOnly && (
                    <Can permiso="ventas.cotizaciones.importar-pdf">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={handlePrecargaPdfPick}
                        sx={{ flexShrink: 0, textTransform: 'none' }}
                      >
                        Precargar desde PDF
                      </Button>
                    </Can>
                  )}
                </Box>
                <FacturaPOSClientePanel
                  cliente={posCliente}
                  onClienteSelect={(cliente) => {
                    if (!isReadOnly) handleClienteSelect(cliente);
                  }}
                  readOnly={isReadOnly}
                  formaDePago={cotizacionVenta.formaDePago || ''}
                  ordenCompra={cotizacionVenta.ordenCompra || ''}
                />
                {modo === 'crear' && !isReadOnly && (
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(e) => void handlePrecargaPdfSelected(e)}
                  />
                )}
              </Box>
              {!isReadOnly && (
                <Box data-tour-id="pos-busqueda">
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Artículos
                  </Typography>
                  <FacturaPOSSearchBar
                    nroLista={obtenerCodigoListaCliente(posCliente)}
                    tipoIvaEmision={tipoIvaEmision}
                    condIvaEmisor={condIvaEmisor}
                    preciosConIva={preciosConIva}
                    disabled={!posCliente}
                    onAgregarProducto={handleAgregarProductoPOS}
                  />
                </Box>
              )}
              <Box data-tour-id="pos-lineas">
                {lineasPOS.length > 0 && (
                  <Stack direction="row" justifyContent="flex-end" mb={0.75}>
                    <Chip
                      label={`${lineasPOS.length} renglon${lineasPOS.length > 1 ? 'es' : ''}`}
                      size="small"
                    />
                  </Stack>
                )}
                <FacturaPOSLineasGrid
                  lineas={lineasPOS}
                  nroLista={obtenerCodigoListaCliente(posCliente)}
                  contextoDescuento={contextoDescuentoDesdeCliente(posCliente, {
                    codigoLista: obtenerCodigoListaCliente(posCliente),
                    nroVendedor: posCliente?.nroVendedor,
                  })}
                  tipoIvaEmision={tipoIvaEmision}
                  condIvaEmisor={condIvaEmisor}
                  productosIvaCache={productosIvaCache}
                  preciosConIva={preciosConIva}
                  readOnly={isReadOnly}
                  onLineaChange={handleLineaChange}
                  onLineaFilled={handleLineaFilled}
                  onProductoIvaLoaded={registrarProductoIva}
                  onEliminarLinea={handleEliminarLinea}
                  onDuplicarLinea={handleDuplicarLinea}
                  onAgregarLineaVacia={handleAgregarLineaVacia}
                  emptyHint="Buscá un artículo arriba para agregarlo a la cotización"
                />
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <FacturaPOSTotalesPanel
                subtotalNeto={subtotalNetoPOS}
                preciosConIva={preciosConIva}
                subtotalDisplay={totalesDisplay.subtotalDisplay}
                importeDesc1Display={totalesDisplay.importeDesc1Display}
                importeDesc2Display={totalesDisplay.importeDesc2Display}
                importeDesc3Display={totalesDisplay.importeDesc3Display}
                porcDesc={cotizacionVenta.porcDesc || 0}
                porcDesc2={cotizacionVenta.porcDesc2 || 0}
                porcDesc3={cotizacionVenta.porcDesc3 || 0}
                onPorcDescChange={handlePorcDescChangePOS}
                impuestos={impuestosPOS}
                montoIVA={ivaPOS}
                total={totalPOS}
                observaciones={cotizacionVenta.descripcion ?? ''}
                onObservacionesChange={(v) =>
                  setCotizacionVenta((prev) => ({ ...prev, descripcion: v }))
                }
                cliente={posCliente}
                fechaEmision={toLocalDateStringYMD(
                  cotizacionVenta.fechaEmision instanceof Date
                    ? cotizacionVenta.fechaEmision
                    : new Date(cotizacionVenta.fechaEmision as string | number),
                )}
                onFechaEmisionChange={(ymd) =>
                  setCotizacionVenta((prev) => {
                    const fechaEmision = parseLocalDate(ymd);
                    const nuevoSugerido = calcularFechaVencimDesdeVtoComprob(
                      fechaEmision,
                      prev.vtoComprob,
                    );
                    const sugeridoAnterior = calcularFechaVencimDesdeVtoComprob(
                      prev.fechaEmision ?? new Date(),
                      prev.vtoComprob,
                    );
                    const mantenerManual = !sameCalendarDay(
                      prev.fechaVencim ?? prev.fechaEmision,
                      sugeridoAnterior,
                    );
                    return {
                      ...prev,
                      fechaEmision,
                      fechaVencim: mantenerManual ? (prev.fechaVencim ?? nuevoSugerido) : nuevoSugerido,
                    };
                  })
                }
                fechaVencim={toLocalDateInputValue(cotizacionVenta.fechaVencim ?? cotizacionVenta.fechaEmision)}
                fechaVencimMin={toLocalDateInputValue(
                  calcularFechaVencimDesdeVtoComprob(
                    cotizacionVenta.fechaEmision,
                    cotizacionVenta.vtoComprob,
                  ),
                )}
                fechaVencimHelperText={
                  cotizacionVenta.vtoComprob != null
                    ? 'Puede editarse, pero no ser anterior a la fecha sugerida'
                    : 'Si no hay plazo comercial, vence en la fecha de emisión'
                }
                onFechaVencimChange={(ymd) =>
                  setCotizacionVenta((prev) => ({ ...prev, fechaVencim: parseLocalDate(ymd) }))
                }
                sucursales={sucursalesDeCompania}
                nroSuc={Number(cotizacionVenta.nroSuc) || 0}
                onNroSucChange={(n) => {
                  sucursalUsuarioEditadaRef.current = true;
                  const suc = sucursales.find((s) => s.nroSuc === n);
                  const idFromSuc = String(suc?.idCompania ?? '').trim();
                  setCotizacionVenta((prev) => ({
                    ...prev,
                    nroSuc: n,
                    ...(idFromSuc ? { idCompania: idFromSuc } : {}),
                  }));
                  if (idFromSuc) persistGlobalIdCompania(idFromSuc);
                }}
                loadingSucursales={loadingSucursales}
                idCompania={cotizacionVenta.idCompania ?? ''}
                onIdCompaniaChange={handleIdCompaniaChange}
                companias={companias}
                nroVendedor={Number(cotizacionVenta.nroVendedor) || 0}
                nombreVendedorFallback={posCliente?.vendedorInfo?.nombreVendedor ?? undefined}
                onNroVendedorChange={(nro, nombre) => {
                  setCotizacionVenta((prev) => ({ ...prev, nroVendedor: nro ?? 0 }));
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                    return {
                      ...prev,
                      nroVendedor: nro,
                      vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                    };
                  });
                  recalcularDescuentosContexto(posCliente, nro || null);
                }}
                codigoLista={
                  obtenerCodigoListaCliente(posCliente) || String(cotizacionVenta.nroLista ?? '').trim()
                }
                onCodigoListaChange={(codigo, descripcion) => {
                  const c = String(codigo ?? '').trim();
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!c) return { ...prev, nroLista: undefined, listaPrecio: undefined };
                    return {
                      ...prev,
                      nroLista: c,
                      listaPrecio: {
                        codigoLista: c,
                        descripcion: descripcion ?? prev.listaPrecio?.descripcion ?? null,
                      },
                    };
                  });
                  setCotizacionVenta((prev) => ({ ...prev, nroLista: c }));
                  if (posCliente) {
                    recalcularDescuentosContexto(
                      c
                        ? { ...posCliente, nroLista: c, listaPrecio: { codigoLista: c, descripcion: descripcion ?? posCliente.listaPrecio?.descripcion ?? null } }
                        : { ...posCliente, nroLista: undefined, listaPrecio: undefined },
                      Number(cotizacionVenta.nroVendedor) || posCliente.nroVendedor || null,
                    );
                  }
                }}
                formaDePago={cotizacionVenta.formaDePago || ''}
                onFormaDePagoChange={(v) => setCotizacionVenta((prev) => ({ ...prev, formaDePago: v }))}
                ordenCompra={cotizacionVenta.ordenCompra || ''}
                onOrdenCompraChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ordenCompra: v }))}
                transporte={cotizacionVenta.transporte || ''}
                onTransporteChange={(v) => setCotizacionVenta((prev) => ({ ...prev, transporte: v }))}
                ubicacion={cotizacionVenta.ubicacion || ''}
                onUbicacionChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ubicacion: v }))}
                saving={loading}
                onGuardar={() => handleSave()}
                cantLineas={lineasPOS.length}
                readOnly={isReadOnly}
                hideDraftButton
                guardadoBloqueado={sinPermisoAlta}
                dataTourPanelId="pos-totales"
                dataTourEmitId="pos-emitir"
                saveLabel={isEditMode ? 'Guardar Cotización' : 'Crear Cotización'}
                emisionNombre={posCliente?.nombre ?? cotizacionVenta.nombre ?? ''}
                onEmisionNombreChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : p));
                  setCotizacionVenta((prev) => ({ ...prev, nombre: v }));
                }}
                emisionCuit={posCliente?.cuit ?? cotizacionVenta.cuit ?? ''}
                onEmisionCuitChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, cuit: v } : p));
                  setCotizacionVenta((prev) => ({ ...prev, cuit: v }));
                }}
                emisionTipoIva={tipoIvaEmision}
                onEmisionTipoIvaChange={(v) => {
                  const tipo = normalizarTipoIvaEmisionCotizacion(v);
                  setPosCliente((p) => (p ? { ...p, tipoIva: tipo } : p));
                  setCotizacionVenta((prev) => ({ ...prev, tipoIVA: tipo }));
                }}
                nroInterno={cotizacionVenta.nroFormulario || Number(cotizacionId) || 0}
              />
            </Box>
          </Grid>
        </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={successDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleSuccessContinuar();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>Cotización creada</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" color="text.secondary">
            La cotización se creó correctamente. Comprobante: {nroCotizacionCreada}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleSuccessContinuar}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <PrecargaCotizacionPdfDialog
        open={precargaPdfOpen}
        loading={precargaPdfLoading}
        precarga={precargaPdf}
        onClose={() => {
          setPrecargaPdfOpen(false);
          setPrecargaPdf(null);
        }}
        onApply={() => void handleApplyPrecargaPdf()}
        onCrearCliente={() => setNuevoClienteDesdePdfOpen(true)}
      />

      <CorregirArticulosGenericosPdfDialog
        open={correccionGenericosOpen}
        items={(precargaPdf?.items || []).filter((item) => item.usoArticuloGenerico)}
        onClose={() => setCorreccionGenericosOpen(false)}
        onConfirm={handleConfirmarGenericos}
      />

      <FacturaPOSNuevoClienteDialog
        open={nuevoClienteDesdePdfOpen}
        onClose={() => setNuevoClienteDesdePdfOpen(false)}
        onCreated={(cli) => {
          handleClienteSelect(cli);
          setNuevoClienteDesdePdfOpen(false);
          enqueueSnackbar('Cliente creado. Aplicá la precarga de nuevo si hace falta.', {
            variant: 'success',
          });
        }}
      />
    </Box>
  );
};

export default CotizacionesVenta;